import { useTranslation } from "react-i18next";
import { FolderOpen, Shield, CheckCircle, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatRelativeTime } from "@/lib/date";
import type { Project } from "@/hooks/useProjects";

export interface ProjectCardStats {
  artifactCoverage: number;
  gatePassRate: number;
  activeMembers: number;
  lastActive: string | null;
}

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
  stats?: ProjectCardStats;
}

export function ProjectCard({ project, onClick, stats }: ProjectCardProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col gap-3 rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/50 hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
            <FolderOpen className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium leading-tight truncate">{project.name}</h3>
            <Badge variant="outline" className="mt-0.5 font-mono text-xs">
              {project.slug}
            </Badge>
          </div>
        </div>
        {project.is_active && (
          <Badge variant="secondary" className="shrink-0 text-xs">
            {t("projects.card.active", "Active")}
          </Badge>
        )}
      </div>

      <p className="text-sm text-muted-foreground line-clamp-2">
        {project.description || "—"}
      </p>

      {stats && (
        <div className="grid grid-cols-3 gap-2 pt-1">
          <div className="flex flex-col items-center rounded-md bg-muted/50 px-2 py-1.5">
            <Shield className="h-3.5 w-3.5 text-muted-foreground mb-0.5" />
            <span className="text-sm font-semibold">{stats.artifactCoverage}%</span>
            <span className="text-[10px] text-muted-foreground">
              {t("projectPortal.overview.artifactCoverage", "Coverage")}
            </span>
          </div>
          <div className="flex flex-col items-center rounded-md bg-muted/50 px-2 py-1.5">
            <CheckCircle className="h-3.5 w-3.5 text-muted-foreground mb-0.5" />
            <span className="text-sm font-semibold">{stats.gatePassRate}%</span>
            <span className="text-[10px] text-muted-foreground">
              {t("projectPortal.overview.gatePassRate", "Gates")}
            </span>
          </div>
          <div className="flex flex-col items-center rounded-md bg-muted/50 px-2 py-1.5">
            <Users className="h-3.5 w-3.5 text-muted-foreground mb-0.5" />
            <span className="text-sm font-semibold">{stats.activeMembers}</span>
            <span className="text-[10px] text-muted-foreground">
              {t("projectPortal.overview.activeMembers", "Members")}
            </span>
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        {formatRelativeTime(project.created_at)}
      </div>
    </button>
  );
}
